import { t } from '@/i18n';
import { DateRangeSchema, DayjsSchema, booleanStringType } from '@/utils';
import { fundSheetTypeEnum, reviewTypeEnum, sheetTypeEnum } from '@/utils/enum';
import { z } from 'zod';
import {
  indicatorDataNatureTypeEnum,
  indicatorTermEnum,
  indicatorTypeEnum,
} from '../_utils/enum';
import { AnalysisAndProjectsSharedSearchSchema } from './shared-search';

export const baseAmountSearchBudgetTypeEnum = z.enum([
  'requestedAmount',
  'finalizedAmountForInitialBudget',
  'finalizedAmountForSupplementaryBudget',
  'finalizedAmountForCarriedForward',
  'finalizedAmountForReserve',
  'finalizedAmount',
  'executionAmount',
]);
export const thresholdAmountSearchBudgetTypeEnum = z.enum([
  ...baseAmountSearchBudgetTypeEnum.options,
  'executionRate',
]);

export const AmountSearchBudgetTypeEnumModifier = {
  requestedAmount: t('all-requested-amount'),
  finalizedAmountForInitialBudget: t('initial-finalized-amount'),
  finalizedAmountForSupplementaryBudget: t('supplementary-finalized-amount'),
  finalizedAmountForCarriedForward: t('carry-over-finalized-amount'),
  finalizedAmountForReserve: t('reserve-finalized-amount'),
  finalizedAmount: t('all-finalized-amount'),
  executionAmount: t('all-executed-amount'),
  executionRate: t('execution-rate'),
} as const satisfies Record<
  z.infer<typeof thresholdAmountSearchBudgetTypeEnum>,
  string
>;

export const candidateOfInspectionByExternalExpert = z.enum(['true', 'false']);
export const CandidateOfInspectionByExternalExpertLabelModifier = {
  true: t('is-candidate-of-inspection-by-external-expert'),
  false: t('is-not-candidate-of-inspection-by-external-expert'),
} as const satisfies Record<
  z.infer<typeof candidateOfInspectionByExternalExpert>,
  string
>;

export const conditionTypeEnum = z.enum(['threshold', 'trend']);
export const conditionTypeLabelModifier = {
  threshold: t('threshold'),
  trend: t('trend'),
} as const satisfies Record<z.infer<typeof conditionTypeEnum>, string>;

export const formulaTrendConditionEnum = z.enum([
  'increaseGte',
  'decreaseGte',
  'increaseGtePercent',
  'decreaseGtePercent',
  'noChange',
]);

export const formulaTrendConditionLabelModifier = {
  increaseGte: t('search.trend-increase-gte'),
  decreaseGte: t('search.trend-decrease-gte'),
  increaseGtePercent: t('search.trend-increase-gte-percent'),
  decreaseGtePercent: t('search.trend-decrease-gte-percent'),
  noChange: t('search.trend-no-change'),
} as const satisfies Record<z.infer<typeof formulaTrendConditionEnum>, string>;

export const initialValueOfProjectSearchForm = (currentFiscalYear: number) => ({
  fiscalYear: currentFiscalYear,
  sheetType: sheetTypeEnum.enum.RS,
  fundSheetType: fundSheetTypeEnum.enum.standard,
  skipAdministrativeReformTeamEvaluation: booleanStringType.enum.false,
  formulaConditions: {
    conditionType: conditionTypeEnum.Enum.threshold,
    baseAmountYear: null,
    baseAmountType: null,
    baseAmountValueIsnull: false,
    baseAmountValueFrom: null,
    baseAmountValueTo: null,
    targetAmountYear: null,
    targetAmountType: null,
    trendConditionValue: null,
    trendCondition: null,
  },
  // 効果発現経路（指標）検索の初期値
  indicatorKeyword: null,
  indicatorKeywordSearchOnlyTitleAndDescription: false,
  indicatorType: null,
  indicatorTerm: null,
  indicatorDataNatureType: null,
  hasOnlyQualitativeIndicator: false,
});

export const AmountSearchConditionSchema = z.array(
  z.object({
    conditionType: conditionTypeEnum,
    baseAmountYear: z.string().nullable(),
    baseAmountType: thresholdAmountSearchBudgetTypeEnum.nullable(),
    // 閾値（threshold）でのみ使用
    baseAmountValueFrom: z.number().nullable(),
    baseAmountValueTo: z.number().nullable(),
    baseAmountValueIsnull: z.boolean().nullable(),
    // 増減（trend）でのみ使用
    targetAmountYear: z.string().nullable(),
    targetAmountType: baseAmountSearchBudgetTypeEnum.nullable(),
    trendConditionValue: z.number().nullable(),
    trendCondition: formulaTrendConditionEnum.nullable(),
  }),
);

const SharedSearch = AnalysisAndProjectsSharedSearchSchema.shape;

// 定義の順番がtagの並び順に影響
export const ProjectSearchSchema = z.object({
  fiscalYear: DayjsSchema(),
  sheetType: SharedSearch.sheetType,
  fundSheetType: fundSheetTypeEnum.nullable(),
  // テキスト検索のキーワード
  q: SharedSearch.q,
  isSearchTargetProjectName: SharedSearch.isSearchTargetProjectName,
  corporateNumber: SharedSearch.corporateNumber,
  categories: SharedSearch.categories,
  implementationMethods: SharedSearch.implementationMethods,
  accountingType: SharedSearch.accountingType,
  accountingTypeIds: SharedSearch.accountingTypeIds,
  skipAdministrativeReformTeamEvaluation: booleanStringType.nullable(),
  projectNumbers: SharedSearch.projectNumbers,
  isPublished: booleanStringType.nullable(),
  period: SharedSearch.period,
  lastImplementedFiscalYearPeriod: DateRangeSchema.nullish(),
  inspectionByExternalExpertReviewType: reviewTypeEnum.array().nullable(),
  isCandidateOfInspectionByExternalExpert:
    candidateOfInspectionByExternalExpert.nullable(),
  // 数式条件（配列化）
  formulaConditions: AmountSearchConditionSchema,
  /**成果目標・成果指標のみ検索対象とする */
  indicatorKeywordSearchOnlyTitleAndDescription: z.boolean(),
  indicatorType: indicatorTypeEnum.nullable(),
  /**定性的指標のみを設定している事業 */
  hasOnlyQualitativeIndicator: z.boolean(),
  indicatorTerm: indicatorTermEnum.nullable(),
  indicatorDataNatureType: indicatorDataNatureTypeEnum.nullable(),
  // 効果発現経路（指標）検索
  indicatorKeyword: z.string().nullable(),
  // cascader項目
  nestedOrganizationIds: SharedSearch.nestedOrganizationIds,
  nestedPolicyIds: SharedSearch.nestedPolicyIds,
  nestedOtherResponsibleOrganizationIds:
    SharedSearch.nestedOtherResponsibleOrganizationIds,
  nestedExpenditureBudgetPurposeIds:
    SharedSearch.nestedExpenditureBudgetPurposeIds,
});

export type ProjectSearchFormType = z.infer<typeof ProjectSearchSchema>;

type ProjectSearchKeysType = keyof typeof ProjectSearchSchema.shape;
export const ProjectSearchKeys = Object.keys(
  ProjectSearchSchema.shape,
) as ProjectSearchKeysType[];
